/**
 * Jyotish Tarang — Muhurta Calculator v1.7
 * Expert tier only — with PDF export
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BuyCreditModal from '../components/BuyCreditModal';

function getToken() {
  return sessionStorage.getItem('access_token') || localStorage.getItem('access_token') || '';
}
function apiFetch(url: string, options?: RequestInit) {
  const token = getToken();
  const headers: Record<string,string> = {'Content-Type':'application/json'};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(url, { ...options, headers: { ...headers, ...(options?.headers||{}) } });
}
async function downloadFile(url: string, filename: string) {
  const token = getToken();
  const resp  = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
  const blob  = await resp.blob();
  const a     = document.createElement('a');
  a.href      = URL.createObjectURL(blob);
  a.download  = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

interface ChoghadiyaSlot { name:string; rating:string; start:string; end:string; }
interface Slot {
  datetime: string; date: string; time: string; weekday: string;
  score: number; verdict: string; in_rahu: boolean; in_yama: boolean;
  in_gulika?: boolean; in_abhijit?: boolean; special_yoga?: string;
  panchanga: { tithi:string; vara:string; nakshatra:string; yoga:string; karana:string; };
  rahu_kaal: { start:string; end:string; };
  gulika_kaal?: { start:string; end:string; };
  abhijit?: { applicable:boolean; start:string; end:string; };
  choghadiya?: { day: ChoghadiyaSlot[]; night: ChoghadiyaSlot[]; };
  breakdown: Record<string, { score:number; max:number; value:string }>;
}
interface Result {
  event_type: string; date_from: string; date_to: string;
  total_slots: number; good_slots: number;
  top_slots: Slot[]; all_slots: Slot[];
}
interface PlaceSuggestion {
  place_name: string; display: string; latitude: number; longitude: number; country: string;
}

export default function MuhurtaPage() {
  const navigate  = useNavigate();
  const { t } = useTranslation();
  const [step,      setStep]      = useState(1);
  const [eventType, setEventType] = useState('wedding');
  const [dateFrom,  setDateFrom]  = useState('');
  const [dateTo,    setDateTo]    = useState('');
  const [timeWin,   setTimeWin]   = useState('any');
  const [useChart,  setUseChart]  = useState(false);
  const [chartId,   setChartId]   = useState<number|''>('');
  const [charts,    setCharts]    = useState<Array<{id:number;name:string}>>([]);
  const [loading,   setLoading]   = useState(false);
  const [result,    setResult]    = useState<Result|null>(null);
  const [error,     setError]     = useState('');
  const [expanded,  setExpanded]  = useState<string|null>(null);
  const [showAll,   setShowAll]   = useState(false);
  const [pdfLoading,setPdfLoading]= useState(false);
  const [pdfUrl,    setPdfUrl]    = useState('');
  const [pdfName,   setPdfName]   = useState('');
  const [showBuyMuhurta, setShowBuyMuhurta] = useState(false);
  // 15 Aug 2026 fix: this page used to silently use the account's saved
  // profile location (or a New Delhi default when unset) for every
  // calculation, with no location field anywhere in the UI -- mobile
  // (MuhurtaScreen.tsx) already asked for the event's actual location.
  // Same event, different platform, could produce different auspicious
  // slots. Now both ask explicitly; see MuhurtaScreen.tsx for the mobile
  // side (unchanged, it was already correct).
  const [placeName,    setPlaceName]    = useState('');
  const [coords,       setCoords]       = useState<{lat:number; lon:number} | null>(null);
  const [suggestions,  setSuggestions]  = useState<PlaceSuggestion[]>([]);
  const [showSuggest,  setShowSuggest]  = useState(false);
  const [placeLoading, setPlaceLoading] = useState(false);
  const placeTimer = React.useRef<any>(null);

  const EVENT_TYPES = [
    { val:'wedding',  label:t('muhurta.eventTypes.wedding') },
    { val:'travel',   label:t('muhurta.eventTypes.travel') },
    { val:'business', label:t('muhurta.eventTypes.business') },
    { val:'property', label:t('muhurta.eventTypes.property') },
    { val:'naming',   label:t('muhurta.eventTypes.naming') },
    { val:'general',  label:t('muhurta.eventTypes.general') },
  ];

  const TIME_WINDOWS = [
    { val:'any',       label:t('muhurta.timeWindows.any') },
    { val:'morning',   label:t('muhurta.timeWindows.morning') },
    { val:'afternoon', label:t('muhurta.timeWindows.afternoon') },
    { val:'evening',   label:t('muhurta.timeWindows.evening') },
  ];

  useEffect(() => {
    const today = new Date();
    const end   = new Date(today);
    end.setDate(end.getDate() + 30);
    setDateFrom(today.toISOString().split('T')[0]);
    setDateTo(end.toISOString().split('T')[0]);
    apiFetch('/api/charts/')
      .then(r=>r.json())
      .then(d=>setCharts(Array.isArray(d)?d:[]))
      .catch(()=>{});
  }, []);

  // Phase 5 (14 Jul 2026, one-time-payments, decision 3): this used to be a
  // hard wall for anyone below Platinum, with no path to the actual feature
  // at all. Muhurta is now reachable by every tier -- non-Platinum users
  // just get a ₹99 BuyCreditModal from handleCalculate() on a 402, instead
  // of being blocked from the page entirely.

  async function onPlaceChange(val: string) {
    setPlaceName(val);
    setCoords(null);
    clearTimeout(placeTimer.current);
    if (val.trim().length < 2) { setSuggestions([]); setShowSuggest(false); return; }
    setPlaceLoading(true);
    placeTimer.current = setTimeout(async () => {
      try {
        const r = await apiFetch(`/api/charts/places/search?q=${encodeURIComponent(val)}`);
        const data = await r.json();
        setSuggestions(data || []);
        setShowSuggest(true);
      } catch {} finally { setPlaceLoading(false); }
    }, 400);
  }

  function selectPlace(p: PlaceSuggestion) {
    setPlaceName(p.display || p.place_name);
    setCoords({ lat: p.latitude, lon: p.longitude });
    setSuggestions([]); setShowSuggest(false);
  }

  async function handleCalculate() {
    if (!coords) { setError(t('muhurta.eventLocationRequired')); return; }
    setLoading(true); setError(''); setResult(null); setPdfUrl(''); setPdfName('');
    try {
      const r = await apiFetch('/api/muhurta/calculate', {
        method:'POST',
        body: JSON.stringify({
          event_type:  eventType,
          date_from:   dateFrom,
          date_to:     dateTo,
          time_window: timeWin,
          latitude:    coords.lat,
          longitude:   coords.lon,
          tz_offset:   5.5,
          chart_id:    useChart && chartId ? chartId : null,
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        // Phase 5 (14 Jul 2026, one-time-payments): a 402 with credit_type
        // means the tier/free-monthly-allowance was exhausted (or this is
        // Free/Gold, credit-only per decision 3/5) but a ₹99 purchase can
        // still unlock this one Muhurta calculation -- offer BuyCreditModal
        // instead of a dead-end error. See muhurta_api.py::calculate_muhurta()'s
        // gate_feature() call (Phase 3, v4.2.2).
        if (data.credit_type === 'muhurta') { setShowBuyMuhurta(true); return; }
        setError(data.error || t('muhurta.errors.calcFailed'));
        return;
      }
      setResult(data);
      setStep(3);
    } catch { setError(t('common.networkError')); }
    finally  { setLoading(false); }
  }

  async function handleMuhurtaPurchased() {
    setShowBuyMuhurta(false);
    await handleCalculate();
  }

  async function handleExportPDF() {
    if (!result) return;
    setPdfLoading(true);
    try {
      const r = await apiFetch('/api/muhurta/pdf', {
        method: 'POST',
        body: JSON.stringify({ muhurta_data: result }),
      });
      const data = await r.json();
      if (!r.ok) { alert(data.error || t('muhurta.errors.pdfFailed')); return; }
      setPdfUrl(data.download_url);
      setPdfName(data.filename);
    } catch { alert(t('muhurta.errors.pdfFailedRetry')); }
    finally  { setPdfLoading(false); }
  }

  function scoreColor(score: number) {
    return score >= 75 ? 'var(--teal)' : score >= 55 ? 'var(--sfb)' : score >= 35 ? '#D97706' : 'var(--red)';
  }
  function scoreBg(score: number) {
    return score >= 75 ? '#F0FDF4' : score >= 55 ? '#FFF7ED' : score >= 35 ? '#FFFBEB' : '#FEF2F2';
  }

  const displaySlots = result ? (showAll ? result.all_slots : result.top_slots) : [];
  const stepLabels = [t('muhurta.steps.event'), t('muhurta.steps.datesTime'), t('muhurta.steps.results')];

  return (
    <div className="page-wrap">
      <div className="container-sm">

        <div style={{marginBottom:24}}>
          <button className="btn btn-ghost btn-sm"
                  onClick={()=>navigate('/dashboard')} style={{marginBottom:8}}>
            ← {t('nav.dashboard')}
          </button>
          <h2 style={{fontFamily:'var(--font-display)',color:'var(--indigo)',marginBottom:4}}>
            🕐 {t('nav.muhurta')}
          </h2>
          <p style={{fontSize:13,color:'var(--ink3)'}}>
            {t('muhurta.subtitle')}
          </p>
        </div>

        {/* Step indicators */}
        {!result && (
          <div style={{display:'flex',gap:8,marginBottom:24}}>
            {stepLabels.map((s,i)=>(
              <div key={i} style={{
                flex:1, textAlign:'center', padding:'8px',
                background: step===i+1 ? 'var(--indigo)' : step>i+1 ? 'var(--teal)' : 'var(--ivory)',
                color: step>=i+1 ? '#fff' : 'var(--ink3)',
                borderRadius:8, fontSize:12, fontWeight:600,
              }}>{i+1}. {s}</div>
            ))}
          </div>
        )}

        {error && <div className="info-box info-red" style={{marginBottom:16}}>{error}</div>}

        {/* Step 1 */}
        {step === 1 && (
          <div className="card">
            <h3 style={{fontSize:14,color:'var(--indigo)',marginBottom:16}}>{t('muhurta.selectEventType')}</h3>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:20}}>
              {EVENT_TYPES.map(e=>(
                <button key={e.val} onClick={()=>setEventType(e.val)}
                        style={{
                          padding:'14px 12px', borderRadius:10, fontSize:13,
                          textAlign:'left', cursor:'pointer',
                          background: eventType===e.val ? 'var(--indigo)' : 'var(--ivory)',
                          color: eventType===e.val ? '#fff' : 'var(--ink)',
                          border: `1.5px solid ${eventType===e.val ? 'var(--indigo)' : 'var(--parch2)'}`,
                          fontFamily:'var(--font-body)', fontWeight:500,
                        }}>
                  {e.label}
                </button>
              ))}
            </div>
            <button className="btn btn-primary" onClick={()=>setStep(2)}>{t('muhurta.next')} →</button>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="card">
            <h3 style={{fontSize:14,color:'var(--indigo)',marginBottom:16}}>{t('muhurta.dateRangePrefs')}</h3>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
              <div className="form-group" style={{marginBottom:0}}>
                <label className="form-label">{t('muhurta.fromDate')} *</label>
                <input className="form-input" type="date" value={dateFrom}
                       onChange={e=>setDateFrom(e.target.value)}/>
              </div>
              <div className="form-group" style={{marginBottom:0}}>
                <label className="form-label">{t('muhurta.toDate')} *</label>
                <input className="form-input" type="date" value={dateTo}
                       onChange={e=>setDateTo(e.target.value)}/>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('muhurta.preferredTime')}</label>
              <select className="form-input" value={timeWin}
                      onChange={e=>setTimeWin(e.target.value)}>
                {TIME_WINDOWS.map(t2=>(
                  <option key={t2.val} value={t2.val}>{t2.label}</option>
                ))}
              </select>
            </div>
            <div style={{
              padding:'12px 14px', background:'var(--ivory)', borderRadius:8,
              border:'0.5px solid var(--parch2)', marginBottom:16,
            }}>
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:useChart?10:0}}>
                <input type="checkbox" id="useChart" checked={useChart}
                       onChange={e=>setUseChart(e.target.checked)}
                       style={{width:16,height:16}}/>
                <label htmlFor="useChart" style={{fontSize:13,color:'var(--ink)',cursor:'pointer'}}>
                  {t('muhurta.useChartLabel')}
                </label>
              </div>
              {useChart && charts.length > 0 && (
                <select className="form-input" value={chartId}
                        onChange={e=>setChartId(Number(e.target.value))}>
                  <option value="">{t('milan.selectChart')}</option>
                  {charts.map(c=>(
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="form-group" style={{position:'relative'}}>
              <label className="form-label">{t('muhurta.eventLocationLabel')} *</label>
              <input className="form-input" type="text" value={placeName}
                     onChange={e=>onPlaceChange(e.target.value)}
                     onFocus={()=>suggestions.length>0 && setShowSuggest(true)}
                     placeholder={t('muhurta.eventLocationPlaceholder') as string}/>
              {showSuggest && suggestions.length > 0 && (
                <div style={{
                  position:'absolute', zIndex:10, left:0, right:0, top:'100%',
                  background:'#fff', border:'1px solid var(--parch2)', borderRadius:8,
                  marginTop:4, maxHeight:200, overflowY:'auto',
                  boxShadow:'0 4px 12px rgba(0,0,0,0.08)',
                }}>
                  {suggestions.map((s,i)=>(
                    <div key={i} onClick={()=>selectPlace(s)}
                         style={{padding:'8px 12px', cursor:'pointer', fontSize:13}}>
                      {s.display || s.place_name}
                    </div>
                  ))}
                </div>
              )}
              {coords && (
                <p style={{fontSize:11,color:'var(--teal)',marginTop:4,marginBottom:0}}>
                  ✓ {placeLoading ? t('common.searching') : placeName}
                </p>
              )}
            </div>
            <div className="info-box info-teal" style={{marginBottom:16,fontSize:12}}>
              📍 {t('muhurta.locationNote')}
            </div>
            <div style={{display:'flex',gap:10}}>
              <button className="btn btn-ghost" onClick={()=>setStep(1)}>← {t('common.back')}</button>
              <button className="btn btn-primary" onClick={handleCalculate}
                      disabled={loading||!dateFrom||!dateTo||!coords}>
                {loading ? t('muhurta.calculating') : `🕐 ${t('muhurta.findTimesButton')} →`}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Results */}
        {step === 3 && result && (
          <div>
            {/* Summary banner */}
            <div style={{
              background:'var(--indigo)', color:'#fff', borderRadius:12,
              padding:'20px 24px', marginBottom:20,
            }}>
              <div style={{fontSize:13,opacity:0.7,marginBottom:8}}>
                {EVENT_TYPES.find(e=>e.val===result.event_type)?.label} · {result.date_from} {t('muhurta.to')} {result.date_to}
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12}}>
                {[
                  {label:t('muhurta.slotsAnalysed'), val:result.total_slots},
                  {label:t('muhurta.auspiciousSlots'), val:result.good_slots},
                  {label:t('muhurta.topScore'), val:result.top_slots[0]?.score+'%' || '—'},
                ].map(({label,val})=>(
                  <div key={label} style={{textAlign:'center'}}>
                    <div style={{fontSize:24,fontWeight:700,color:'#F4C430'}}>{val}</div>
                    <div style={{fontSize:11,opacity:0.7}}>{label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Slots */}
            <div className="card" style={{marginBottom:16}}>
              <h3 style={{fontSize:14,color:'var(--indigo)',marginBottom:4}}>
                {t('muhurta.topAuspiciousMuhurtas')}
              </h3>
              <p style={{fontSize:12,color:'var(--ink3)',marginBottom:16}}>
                {t('muhurta.tapForDetails')}
              </p>
              {displaySlots.map((slot,i)=>(
                <div key={slot.datetime} style={{marginBottom:10}}>
                  <div onClick={()=>setExpanded(expanded===slot.datetime?null:slot.datetime)}
                       style={{
                         padding:'14px 16px', borderRadius:10, cursor:'pointer',
                         background: scoreBg(slot.score),
                         border:`1.5px solid ${scoreColor(slot.score)}`,
                         opacity: slot.in_rahu || slot.in_yama ? 0.65 : 1,
                       }}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <div>
                        {i<3 && <span style={{
                          fontSize:10,background:'var(--indigo)',color:'#fff',
                          padding:'2px 8px',borderRadius:10,marginRight:8,
                        }}>#{i+1}</span>}
                        <span style={{fontSize:15,fontWeight:700,color:'var(--indigo)',fontFamily:'var(--font-mono)'}}>
                          {slot.weekday}, {slot.date}
                        </span>
                        <span style={{fontSize:13,color:'var(--ink2)',marginLeft:8}}>{t('muhurta.at')} {slot.time}</span>
                        {slot.in_rahu && <span style={{marginLeft:8,fontSize:11,color:'var(--red)',fontWeight:600}}>⚠ {t('muhurta.rahuKaal')}</span>}
                        {slot.in_yama && <span style={{marginLeft:8,fontSize:11,color:'#D97706',fontWeight:600}}>⚠ {t('muhurta.yamaganda')}</span>}
                      </div>
                      <div style={{textAlign:'right'}}>
                        <div style={{fontSize:20,fontWeight:700,color:scoreColor(slot.score)}}>{slot.score}</div>
                        <div style={{fontSize:10,color:'var(--ink3)'}}>/ 100</div>
                      </div>
                    </div>
                    <div style={{fontSize:12,color:'var(--ink2)',marginTop:6}}>
                      {slot.panchanga.tithi} · {slot.panchanga.nakshatra} · {slot.panchanga.yoga}
                    </div>
                    <div style={{height:3,background:'var(--parch2)',borderRadius:2,marginTop:8}}>
                      <div style={{height:3,borderRadius:2,width:`${slot.score}%`,background:scoreColor(slot.score)}}/>
                    </div>
                  </div>
                  {expanded===slot.datetime && (
                    <div style={{
                      padding:'14px 16px', background:'var(--ivory)',
                      borderRadius:'0 0 10px 10px', border:'1px solid var(--parch2)', borderTop:'none',
                    }}>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12}}>
                        {Object.entries(slot.panchanga).map(([k,v])=>(
                          <div key={k} style={{fontSize:12}}>
                            <span style={{color:'var(--ink3)',textTransform:'capitalize'}}>{k}: </span>
                            <strong style={{color:'var(--ink)'}}>{v as string}</strong>
                          </div>
                        ))}
                        <div style={{fontSize:12}}>
                          <span style={{color:'var(--ink3)'}}>{t('muhurta.rahuKaal')}: </span>
                          <strong style={{color:'var(--red)'}}>{slot.rahu_kaal.start}–{slot.rahu_kaal.end}</strong>
                        </div>
                        {slot.gulika_kaal && (
                          <div style={{fontSize:12}}>
                            <span style={{color:'var(--ink3)'}}>{t('muhurta.gulikaKaal')}: </span>
                            <strong style={{color:'var(--red)'}}>{slot.gulika_kaal.start}–{slot.gulika_kaal.end}</strong>
                          </div>
                        )}
                        {slot.abhijit?.applicable && (
                          <div style={{fontSize:12}}>
                            <span style={{color:'var(--ink3)'}}>{t('muhurta.abhijitMuhurta')}: </span>
                            <strong style={{color:'var(--teal)'}}>{slot.abhijit.start}–{slot.abhijit.end}</strong>
                          </div>
                        )}
                        {slot.special_yoga && (
                          <div style={{fontSize:12,gridColumn:'1 / -1'}}>
                            <span style={{color:'var(--gold)',fontWeight:600}}>✨ {slot.special_yoga}</span>
                          </div>
                        )}
                      </div>
                      {slot.choghadiya && (
                        <div style={{borderTop:'0.5px solid var(--parch2)',paddingTop:10,marginBottom:10}}>
                          <div style={{fontSize:11,color:'var(--ink3)',marginBottom:6,fontWeight:600}}>{t('muhurta.choghadiyaDay')}</div>
                          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:4}}>
                            {slot.choghadiya.day.map((c,i)=>(
                              <div key={i} style={{
                                fontSize:10,padding:'4px 2px',textAlign:'center',borderRadius:4,
                                background: c.rating==='good' ? 'var(--sfl)' : c.rating==='avoid' ? '#fcdede' : 'var(--cream)',
                                color: c.rating==='good' ? 'var(--teal)' : c.rating==='avoid' ? 'var(--red)' : 'var(--ink2)',
                              }}>
                                <div style={{fontWeight:600}}>{c.name}</div>
                                <div style={{fontSize:9}}>{c.start}-{c.end}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div style={{borderTop:'0.5px solid var(--parch2)',paddingTop:10}}>
                        <div style={{fontSize:11,color:'var(--ink3)',marginBottom:6,fontWeight:600}}>{t('muhurta.scoreBreakdown')}</div>
                        {Object.entries(slot.breakdown).map(([k,v])=>{
                          const bd = v as any;
                          return (
                            <div key={k} style={{display:'flex',justifyContent:'space-between',fontSize:11,marginBottom:3}}>
                              <span style={{color:'var(--ink2)',textTransform:'capitalize'}}>
                                {k.replace('_',' ')}: {bd.value}
                              </span>
                              <span style={{fontWeight:600,color:bd.score>0?'var(--teal)':bd.score<0?'var(--red)':'var(--ink3)'}}>
                                {bd.score>0?'+':''}{bd.score}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {result.all_slots.length > 10 && (
                <button className="btn btn-ghost btn-full"
                        onClick={()=>setShowAll(!showAll)} style={{marginTop:8}}>
                  {showAll ? t('muhurta.showTop10') : t('muhurta.showAllSlots', { count: result.all_slots.length })}
                </button>
              )}
            </div>

            {/* Actions */}
            <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
              <button className="btn btn-secondary"
                      onClick={()=>{setResult(null);setStep(1);setShowAll(false);setPdfUrl('');setPdfName('');}}>
                {t('muhurta.newSearch')}
              </button>
              {pdfUrl ? (
                <button className="btn btn-primary" onClick={()=>downloadFile(pdfUrl,pdfName)}>
                  ⬇ {t('muhurta.downloadMuhurtaPdf')}
                </button>
              ) : (
                <button className="btn btn-primary" onClick={handleExportPDF} disabled={pdfLoading}>
                  {pdfLoading ? t('milan.generatingPdf') : `📄 ${t('milan.exportPdf')}`}
                </button>
              )}
              <button className="btn btn-ghost" onClick={()=>navigate('/ask')}>
                🔮 {t('muhurta.askAiGuidance')}
              </button>
            </div>
          </div>
        )}
      </div>
      <BuyCreditModal
        open={showBuyMuhurta}
        creditType="muhurta"
        onClose={() => setShowBuyMuhurta(false)}
        onPurchased={handleMuhurtaPurchased}
      />
    </div>
  );
}
